import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Church, BadgeCheck, MapPin, Clock, Globe, Users, Plus, ArrowLeft, Search, Heart, UserPlus, UserCheck, ShieldCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifiedBadge = ({ verified, es }) =>
  verified ? (
    <span className="inline-flex items-center gap-1 text-xs text-sky-500 font-medium" title={es ? 'Verificada' : 'Verified'}>
      <BadgeCheck className="w-4 h-4" /> {es ? 'Verificada' : 'Verified'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={es ? 'Sin verificar' : 'Unverified'}>
      {es ? 'Sin verificar' : 'Unverified'}
    </span>
  );

const ChurchLogo = ({ church, size = 'w-12 h-12' }) => (
  <Avatar className={size}>
    {church.logo_url ? <AvatarImage src={church.logo_url} alt={church.name} /> : null}
    <AvatarFallback className="bg-primary/10 text-primary"><Church className="w-5 h-5" /></AvatarFallback>
  </Avatar>
);

const emptyForm = { name: '', denomination: '', city: '', country: '', description: '', logo_url: '', service_times: '', website: '', phone: '', email: '' };

export const ChurchesView = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const es = language === 'es';
  const isSuperAdmin = user?.role === 'super_admin';

  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // church detail
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async (query = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/churches`, { params: query ? { q: query } : {}, withCredentials: true });
      setList(data.churches || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openChurch = async (id) => {
    try {
      const [{ data: c }, { data: p }, { data: m }] = await Promise.all([
        axios.get(`${API}/churches/${id}`, { withCredentials: true }),
        axios.get(`${API}/churches/${id}/posts`, { withCredentials: true }),
        axios.get(`${API}/churches/${id}/members`, { withCredentials: true }),
      ]);
      setSelected({ ...c, posts: p.posts || [], members: m.members || [] });
    } catch (e) { toast.error(es ? 'No se pudo abrir la iglesia' : 'Could not open church'); }
  };

  const refreshSelected = () => selected && openChurch(selected.id);

  const createChurch = async () => {
    if (!form.name.trim()) { toast.error(es ? 'El nombre es obligatorio' : 'Name is required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/churches`, form, { withCredentials: true });
      toast.success(es ? 'Iglesia creada — pendiente de verificación' : 'Church created — pending verification');
      setShowCreate(false); setForm(emptyForm);
      await loadList();
      openChurch(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || (es ? 'No se pudo crear' : 'Could not create church')); }
    setSaving(false);
  };

  const toggle = async (type) => {
    if (!user) { toast.error(es ? 'Inicia sesión primero' : 'Please sign in first'); return; }
    const c = selected;
    const on = type === 'follow' ? c.is_following : c.is_member;
    try {
      await axios({ method: on ? 'delete' : 'post', url: `${API}/churches/${c.id}/${type === 'follow' ? 'follow' : 'join'}`, withCredentials: true });
      await refreshSelected(); await loadList(q);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const verify = async () => {
    try { await axios.post(`${API}/churches/${selected.id}/verify`, {}, { withCredentials: true }); await refreshSelected(); }
    catch (e) { toast.error('Error'); }
  };

  const [postText, setPostText] = useState('');
  const submitPost = async () => {
    if (!postText.trim()) return;
    try {
      await axios.post(`${API}/churches/${selected.id}/posts`, { text: postText }, { withCredentials: true });
      setPostText(''); await refreshSelected();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  /* ---------------- Detail view ---------------- */
  if (selected) {
    const c = selected;
    return (
      <div className="space-y-4" data-testid="church-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="church-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> {es ? 'Todas las iglesias' : 'All churches'}
        </Button>

        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-4">
              <ChurchLogo church={c} size="w-16 h-16" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold">{c.name}</h2>
                  <VerifiedBadge verified={c.verified} es={es} />
                </div>
                <p className="text-sm text-muted-foreground">{[c.denomination, c.city, c.country].filter(Boolean).join(' · ')}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.member_count} {es ? 'miembros' : 'members'}</span>
                  <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {c.follower_count} {es ? 'seguidores' : 'followers'}</span>
                </div>
              </div>
            </div>

            {c.description && <p className="text-sm">{c.description}</p>}
            <div className="space-y-1 text-sm text-muted-foreground">
              {c.service_times && <p className="flex items-center gap-2"><Clock className="w-4 h-4" /> {c.service_times}</p>}
              {c.website && <p className="flex items-center gap-2"><Globe className="w-4 h-4" /> <a className="text-primary underline" href={c.website} target="_blank" rel="noreferrer">{c.website}</a></p>}
              {c.city && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {[c.city, c.country].filter(Boolean).join(', ')}</p>}
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button variant={c.is_following ? 'secondary' : 'default'} size="sm" onClick={() => toggle('follow')} data-testid="church-follow-btn">
                {c.is_following ? <><Heart className="w-4 h-4 mr-1 fill-current" /> {es ? 'Siguiendo' : 'Following'}</> : <><Heart className="w-4 h-4 mr-1" /> {es ? 'Seguir' : 'Follow'}</>}
              </Button>
              <Button variant={c.is_member ? 'secondary' : 'outline'} size="sm" onClick={() => toggle('join')} data-testid="church-join-btn">
                {c.is_member ? <><UserCheck className="w-4 h-4 mr-1" /> {es ? 'Miembro' : 'Member'}</> : <><UserPlus className="w-4 h-4 mr-1" /> {es ? 'Unirse' : 'Join'}</>}
              </Button>
              {isSuperAdmin && (
                <Button variant="outline" size="sm" onClick={verify} data-testid="church-verify-btn">
                  <ShieldCheck className="w-4 h-4 mr-1" /> {c.verified ? (es ? 'Quitar verificación' : 'Unverify') : (es ? 'Verificar' : 'Verify')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Feed */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-sm">{es ? 'Publicaciones' : 'Church updates'}</h3>
            {c.is_admin && (
              <div className="flex gap-2">
                <Textarea value={postText} onChange={(e) => setPostText(e.target.value)} placeholder={es ? 'Comparte una actualización con tu congregación…' : 'Share an update with your congregation…'} rows={2} data-testid="church-post-input" />
                <Button onClick={submitPost} data-testid="church-post-submit" className="self-end"><Send className="w-4 h-4" /></Button>
              </div>
            )}
            {c.posts.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="church-feed-empty">{es ? 'Aún no hay publicaciones.' : 'No updates yet.'}</p>
            ) : (
              <div className="space-y-3">
                {c.posts.map((p) => (
                  <div key={p.id} className="border-b border-border/50 pb-3 last:border-0" data-testid="church-post">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="w-7 h-7"><AvatarImage src={p.author?.picture} /><AvatarFallback>{(p.author?.name || '?').slice(0, 1)}</AvatarFallback></Avatar>
                      <span className="text-sm font-medium">{p.author?.name}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{p.text}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ---------------- Directory view ---------------- */
  return (
    <div className="space-y-4" data-testid="churches-view">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadList(q)} placeholder={es ? 'Buscar por nombre, ciudad o denominación' : 'Search by name, city or denomination'} className="pl-9" data-testid="church-search-input" />
        </div>
        {user && (
          <Button onClick={() => setShowCreate(true)} data-testid="church-create-btn"><Plus className="w-4 h-4 mr-1" /> {es ? 'Crear' : 'Create'}</Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">{es ? 'Cargando…' : 'Loading…'}</p>
      ) : list.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="churches-empty">
          {es ? 'No se encontraron iglesias. ¡Crea la primera!' : 'No churches found. Be the first to add one!'}
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {list.map((c) => (
            <Card key={c.id} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => openChurch(c.id)} data-testid={`church-card-${c.id}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <ChurchLogo church={c} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{c.name}</span>
                    {c.verified && <BadgeCheck className="w-4 h-4 text-sky-500 shrink-0" />}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{[c.denomination, c.city].filter(Boolean).join(' · ')}</p>
                  <p className="text-xs text-muted-foreground">{c.member_count} {es ? 'miembros' : 'members'} · {c.follower_count} {es ? 'seguidores' : 'followers'}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="church-create-dialog">
          <DialogHeader><DialogTitle>{es ? 'Crear una iglesia' : 'Create a church'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              ['name', es ? 'Nombre *' : 'Name *'],
              ['denomination', es ? 'Denominación' : 'Denomination'],
              ['city', es ? 'Ciudad' : 'City'],
              ['country', es ? 'País' : 'Country'],
              ['service_times', es ? 'Horario de servicios' : 'Service times'],
              ['website', es ? 'Sitio web' : 'Website'],
              ['logo_url', es ? 'URL del logo' : 'Logo URL'],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="text-xs text-muted-foreground">{label}</label>
                <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`church-form-${key}`} />
              </div>
            ))}
            <div>
              <label className="text-xs text-muted-foreground">{es ? 'Descripción' : 'Description'}</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="church-form-description" />
            </div>
            <Button onClick={createChurch} disabled={saving} className="w-full" data-testid="church-form-submit">
              {saving ? (es ? 'Creando…' : 'Creating…') : (es ? 'Crear iglesia' : 'Create church')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChurchesView;
